import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useCookies } from 'react-cookie';
import {
  DecisionTreeRunWithParentFieldsFragment,
  ListDecisionTreesQuery,
  useGetDecisionTreeRunQuery, useStartDecisionTreeRunMutation, useStopDecisionTreeRunMutation, useUpdateDecisionTreeRunHistoryMutation,
  useWriteDecisionTreeRunCommentMutation,
} from '../../../../generated/core-api/graphql-react-query';
import useSessionContext from '../../../LiveContext/SessionContextProvider/useSessionContext';
import useProjectIdContext from '../../ProjectContexts/ProjectIdContext/useProjectIdContext';
import useDecisionTreeContext from '../DecisionTreeContext/useDecisionTreeContext';
import { DecisionTreeRunContext } from './useDecisionTreeRunContext';

function useRunUpdater() {
  const queryClient = useQueryClient();
  const { projectId } = useProjectIdContext();

  const updateTreeRun = React.useCallback((value: DecisionTreeRunWithParentFieldsFragment) => {
    queryClient.setQueryData(['getDecisionTreeRun', { input: { id: value.id } }], { getDecisionTreeRun: value });

    queryClient.setQueryData(['listDecisionTrees', { input: { id: projectId ?? '' } }], (prev: ListDecisionTreesQuery | null) => {
      if (!prev?.getProject) {
        return prev;
      }
      const trees = prev.getProject.decisionTrees ?? [];
      const updatedTrees = trees.map((tree) => {
        if (tree.id === value.decisionTreeId) {
          return {
            ...tree,
            latestRun: value,
          };
        }
        return tree;
      });
      return {
        getProject: {
          ...prev.getProject,
          decisionTrees: updatedTrees,
        },
      };
    });
  }, [projectId]);

  const update = React.useCallback((value: DecisionTreeRunWithParentFieldsFragment) => {
    updateTreeRun(value);

    if (value.parentId && value.parent) {
      updateTreeRun(value.parent);
    }
  }, [updateTreeRun]);

  return update;
}

export default function DecisionTreeRunContextProvider({ children }: { children: React.ReactNode }) {
  const [cookies, setCookies, removeCookies] = useCookies(['decisionTreeRunId']);
  const { projectId } = useProjectIdContext();
  const runId = React.useMemo(() => cookies.decisionTreeRunId, [cookies.decisionTreeRunId]);
  const setRunId = React.useCallback((value: string | null | undefined) => {
    if (value) {
      setCookies('decisionTreeRunId', value);
    } else {
      removeCookies('decisionTreeRunId');
    }
  }, []);

  const decisionTreeRun = useGetDecisionTreeRunQuery({ input: { id: runId ?? '' } }, { enabled: !!runId, staleTime: Infinity });
  const { isMounted, toggleMounted } = useDecisionTreeContext();

  const { sessionId } = useSessionContext();

  const queryClient = useQueryClient();
  const updateRun = useRunUpdater();

  const updateDecisionTreeRunHistoryMutation = useUpdateDecisionTreeRunHistoryMutation({
    onSuccess(data) {
      updateRun(data.decisionTree.run.updateHistory);
    },
  });

  const startRunMutation = useStartDecisionTreeRunMutation({
    onSuccess(data) {
      if (!isMounted) {
        toggleMounted();
      }
      updateRun(data.decisionTree.run.start);
      setRunId(data.decisionTree.run.start.id);
    },
  });

  const stopRunMutation = useStopDecisionTreeRunMutation({
    onSuccess(data) {
      updateRun(data.decisionTree.run.stop);
      setRunId(undefined);
    },
  });

  const writeCommentMutation = useWriteDecisionTreeRunCommentMutation({
    onSuccess(data) {
      updateRun(data.decisionTree.run.writeComment);
    },
  });

  const chooseChild = React.useCallback((nodeId: string, value: string, childId: string) => {
    if (!decisionTreeRun.data) {
      throw new Error('Decision tree run not found');
    }
    const currentHistory = decisionTreeRun.data.getDecisionTreeRun.history;
    if (currentHistory.length === 0) {
      throw new Error('No history found');
    }

    const historyUpToNow = [];
    currentHistory.some((h) => {
      if (h.nodeId === nodeId) {
        return true;
      }
      historyUpToNow.push({ nodeId: h.nodeId, value: h.value });
      return false;
    });

    const updatedHistory = [...historyUpToNow, { nodeId, value }, { nodeId: childId }];

    updateDecisionTreeRunHistoryMutation.mutate({
      input: {
        id: runId ?? '',
        history: updatedHistory,
      },
    });
  }, [decisionTreeRun.data]);

  const toggleSequenceValue = React.useCallback((value: string) => {
    if (!decisionTreeRun.data) {
      throw new Error('Decision tree run not found');
    }
    const currentHistory = decisionTreeRun.data.getDecisionTreeRun.history;
    if (currentHistory.length === 0) {
      throw new Error('No history found');
    }
    const lastNode = currentHistory.slice(-1)[0];
    const currentValues = lastNode.sequenceValues ?? [];
    const updatedValues = currentValues.includes(value) ? currentValues.filter((v) => v !== value) : [...currentValues, value];
    const updatedHistory = [
      ...currentHistory.slice(0, -1).map((node) => ({ nodeId: node.nodeId, value: node.value })),
      {
        nodeId: lastNode.nodeId,
        sequenceValues: updatedValues,
      },
    ];

    updateDecisionTreeRunHistoryMutation.mutate({
      input: {
        id: runId ?? '',
        history: updatedHistory,
      },
    });
  }, [decisionTreeRun.data]);

  const start = React.useCallback((id: string, parentId: string, isPreviewRun: boolean) => {
    startRunMutation.mutate({
      input: {
        id,
        projectId,
        parentId,
        isPreviewRun,
        sessionId,
      },
    });
    if (parentId) {
      queryClient.invalidateQueries(['getDecisionTreeRun', { input: { id: parentId } }]);
    }
  }, [sessionId]);

  const writeComment = React.useCallback((nodeId: string, userComment: string) => {
    writeCommentMutation.mutate({
      input: {
        nodeId,
        runId,
        userComment,
      },
    });
  }, [runId]);

  const stop = React.useCallback(() => {
    if (runId) {
      stopRunMutation.mutate({ input: { id: runId } });
    }
  }, [runId]);

  const value = React.useMemo(() => ({
    runId: runId ?? null,
    run: decisionTreeRun.data?.getDecisionTreeRun ?? null,
    resume(id: string) { setRunId(id); },
    writeComment,
    pause() { setRunId(null); },
    start,
    stop,
    chooseChild,
    toggleSequenceValue,
  }), [sessionId, chooseChild, runId, decisionTreeRun.data?.getDecisionTreeRun]);

  return (
    <DecisionTreeRunContext.Provider value={value}>
      {children}
    </DecisionTreeRunContext.Provider>
  );
}
